import './Bookmarks.scss';
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import AuthContext from "../../context/AuthContext";
import SnackbarContext from "../../context/SnackbarContext";
import { API_BASE_URL } from '../../config';
import { authHeaders } from '../../utils/api';

function absoluteUrl(path) {
    if (!path) return '';
    if (/^https?:/i.test(path)) return path;
    return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

const chapterName = (title, order) => title || `الفصل ${(order ?? 0) + 1}`;

export default function Bookmarks() {
    const { authTokens } = useContext(AuthContext);
    const { setSnackbar } = useContext(SnackbarContext);
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const tab = params.get('tab') === 'highlights' ? 'highlights' : 'bookmarks';

    const [bookmarks, setBookmarks] = useState([]);
    const [highlights, setHighlights] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [b, h] = await Promise.all([
                    fetch(`${API_BASE_URL}/api/books/bookmarks/`, { headers: authHeaders(authTokens) }),
                    fetch(`${API_BASE_URL}/api/books/highlights/`, { headers: authHeaders(authTokens) }),
                ]);
                if (!b.ok || !h.ok) throw new Error(`HTTP ${b.status}/${h.status}`);
                const [bd, hd] = await Promise.all([b.json(), h.json()]);
                if (!cancelled) {
                    setBookmarks(bd.bookmarks ?? []);
                    setHighlights(hd.highlights ?? []);
                }
            } catch {
                if (!cancelled) setSnackbar({ content: 'تعذر تحميل العلامات', success: false });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [authTokens, setSnackbar]);

    const removeBookmark = async (id) => {
        try {
            await fetch(`${API_BASE_URL}/api/books/bookmarks/?id=${id}`, {
                method: 'DELETE',
                headers: authHeaders(authTokens),
            });
            setBookmarks(bs => bs.filter(b => b.id !== id));
        } catch {
            setSnackbar({ content: 'تعذر حذف العلامة', success: false });
        }
    };

    const removeHighlight = async (id) => {
        try {
            await fetch(`${API_BASE_URL}/api/books/highlights/?id=${id}`, {
                method: 'DELETE',
                headers: authHeaders(authTokens),
            });
            setHighlights(hs => hs.filter(h => h.id !== id));
        } catch {
            setSnackbar({ content: 'تعذر حذف التظليل', success: false });
        }
    };

    const openBookmark = (b) =>
        navigate(`/read/${b.book}${b.chapter ? `?chapter=${b.chapter}` : ''}`);

    const openHighlight = (h) =>
        navigate(`/read/${h.book}?chapter=${h.chapter}&hl=${h.id}`);

    const empty = (
        <div className='bm-empty-state'>
            <p>{tab === 'highlights' ? 'لا توجد تظليلات بعد. ظلّل أي نص أثناء القراءة لحفظه هنا.' : 'لا توجد علامات بعد.'}</p>
            <Link to='/library' className='bm-cta'>تصفّح المكتبة</Link>
        </div>
    );

    return (
        <>
        <Navbar />
        <main className='bm-page'>
            <div className='bm-container'>
                <h1 className='bm-title'>علاماتي المرجعية</h1>

                <div className='bm-tabs' role='tablist'>
                    <button
                        role='tab'
                        aria-selected={tab === 'bookmarks'}
                        className={tab === 'bookmarks' ? 'is-active' : ''}
                        onClick={() => setParams({})}
                    >
                        العلامات {!loading && <span className='bm-tab-count'>{bookmarks.length}</span>}
                    </button>
                    <button
                        role='tab'
                        aria-selected={tab === 'highlights'}
                        className={tab === 'highlights' ? 'is-active' : ''}
                        onClick={() => setParams({ tab: 'highlights' })}
                    >
                        التظليلات {!loading && <span className='bm-tab-count'>{highlights.length}</span>}
                    </button>
                </div>

                {loading ? (
                    <p className='bm-empty'>جارٍ التحميل…</p>
                ) : tab === 'bookmarks' ? (
                    bookmarks.length === 0 ? empty : (
                        <ul className='bm-list'>
                            {bookmarks.map(b => (
                                <li key={b.id} className='bm-item'>
                                    <button className='bm-card' onClick={() => openBookmark(b)}>
                                        <div className='bm-thumb'>
                                            {b.book_cover ? (
                                                <img src={absoluteUrl(b.book_cover)} alt={b.book_title} />
                                            ) : (
                                                <span>📖</span>
                                            )}
                                        </div>
                                        <div className='bm-info'>
                                            <h3>{b.book_title}</h3>
                                            <p className='bm-author'>{b.book_display_author}</p>
                                            {b.chapter && (
                                                <p className='bm-chapter'>
                                                    عند: {chapterName(b.chapter_title, b.chapter_order)}
                                                </p>
                                            )}
                                            <p className='bm-date'>
                                                {new Date(b.date_created).toLocaleDateString('ar-EG')}
                                            </p>
                                        </div>
                                    </button>
                                    <button
                                        className='bm-remove'
                                        onClick={() => removeBookmark(b.id)}
                                        aria-label='حذف العلامة'
                                        title='حذف'
                                    >
                                        ×
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )
                ) : (
                    highlights.length === 0 ? empty : (
                        <ul className='bm-list'>
                            {highlights.map(h => (
                                <li key={h.id} className='bm-item'>
                                    <button className='bm-card bm-card--highlight' onClick={() => openHighlight(h)}>
                                        <div className='bm-thumb'>
                                            {h.book_cover ? (
                                                <img src={absoluteUrl(h.book_cover)} alt={h.book_title} />
                                            ) : (
                                                <span>📖</span>
                                            )}
                                        </div>
                                        <div className='bm-info'>
                                            <h3>{h.book_title}</h3>
                                            <p className='bm-author'>
                                                {h.book_display_author} · {chapterName(h.chapter_title, h.chapter_order)}
                                            </p>
                                            <q className='bm-quote'>{h.text}</q>
                                            <p className='bm-date'>
                                                {new Date(h.date_created).toLocaleDateString('ar-EG')}
                                            </p>
                                        </div>
                                    </button>
                                    <button
                                        className='bm-remove'
                                        onClick={() => removeHighlight(h.id)}
                                        aria-label='حذف التظليل'
                                        title='حذف'
                                    >
                                        ×
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )
                )}
            </div>
        </main>
        <Footer />
        </>
    );
}
